import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { EmptyState } from '@/lib/base/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import type { TimelineItem } from './collapseAssociationEvents';
import { useViewStateLookups } from './context/TimelineLookupsContext';
import { FallbackEvent } from './events/FallbackEvent';
import { getItemComponent } from './items/itemRegistry';
import { RelatedThreadsTruncatedNotice } from './RelatedThreadsTruncatedNotice';
import { resolveNodeEntityType } from './resolveNodeEntityType';
import type { TimelineTreeNode } from './timelineTree';
import { buildTimelineTree } from './timelineTree';
import { useInfiniteScroll } from './useInfiniteScroll';

// A single fixed per-level indent. Because each node renders its children
// inside its own (already-indented) container, this margin accumulates through
// DOM nesting — so indentation scales linearly with depth without a cap and
// without dynamically-built class strings (Tailwind v4 static extraction).
const NESTED_INDENT_CLASS = 'ml-6';

// Stable, unique React key for a tree node among its siblings. Prefer the first
// event's id (stable across reorders); when it's missing — malformed or partial
// items — fall back to the item type plus the sibling index so two type-only
// keys never collide (which would let React reuse a row for the wrong event).
function treeNodeKey(node: TimelineTreeNode, index: number): string {
  const eventId = node.item.events[0]?.event_id;
  return eventId != null ? String(eventId) : `${node.item.type}-${index}`;
}

function TimelineTreeNodeView({
  node,
  depth,
  entityType,
}: {
  node: TimelineTreeNode;
  depth: number;
  entityType: string;
}) {
  const ItemComponent = getItemComponent(node.item.type);
  const nodeEntityType = resolveNodeEntityType(node, entityType);
  return (
    <div className={depth > 0 ? NESTED_INDENT_CLASS : undefined}>
      <div className={node.isContext ? 'relative opacity-60' : 'relative'}>
        <ItemErrorBoundary item={node.item} entityType={nodeEntityType}>
          <ItemComponent
            item={node.item}
            entityType={nodeEntityType}
            isContext={node.isContext}
          />
        </ItemErrorBoundary>
      </div>
      {node.children.length > 0 && (
        <div className='mt-0'>
          {node.children.map((child, index) => (
            <TimelineTreeNodeView
              key={treeNodeKey(child, index)}
              node={child}
              depth={depth + 1}
              entityType={entityType}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface TimelineEventListProps {
  items: TimelineItem[];
  entityType: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  /**
   * When true, suppress the "Beginning of timeline" footer. Used by previews
   * that truncate the list client-side (e.g. RecentActivityPreview with a
   * `limit` prop), where the footer would be misleading because the user is
   * not actually at the end of the underlying event stream.
   */
  hideEndOfTimelineMessage?: boolean;
  /**
   * When true, the backend capped this contact's related email-thread fan-out
   * to the most-recently-active threads (RELATED_THREAD_CAP). Surfaces a passive
   * notice at the bottom (oldest end) of the timeline so dropped older threads
   * aren't silently missing. Only ever true for contacts.
   */
  relatedThreadsTruncated?: boolean;
}

/**
 * Timeline event list with infinite scroll
 *
 * Renders timeline items using the item registry to get appropriate components.
 * Uses IntersectionObserver for auto-loading more events.
 * Must be wrapped in ViewStateLookupsProvider.
 */
export function TimelineEventList({
  items,
  entityType,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  hideEndOfTimelineMessage = false,
  relatedThreadsTruncated = false,
}: TimelineEventListProps) {
  const { sentinelRef, isIntersecting } = useInfiniteScroll();
  const { lookups } = useViewStateLookups();

  // Auto-load at most ONCE per intersection episode. The sentinel must
  // leave the viewport (i.e. the user actually scrolls) before another
  // auto-load can fire. Without this gate, a sentinel that stays within
  // the observer's rootMargin after a page renders re-triggers the effect
  // as soon as `isFetchingNextPage` flips back to false — sequentially
  // draining every page of an entity's history on open. If a loaded page
  // adds too little height to push the sentinel away, the user still has
  // the explicit "Load more events" button below.
  const autoLoadConsumedRef = useRef(false);
  useEffect(() => {
    if (!isIntersecting) {
      // Sentinel left view — re-arm for the next genuine scroll-to-bottom.
      autoLoadConsumedRef.current = false;
      return;
    }
    if (hasNextPage && !isFetchingNextPage && !autoLoadConsumedRef.current) {
      autoLoadConsumedRef.current = true;
      onLoadMore();
    }
  }, [isIntersecting, hasNextPage, isFetchingNextPage, onLoadMore]);

  const tree = useMemo(
    () => buildTimelineTree(items, lookups.events),
    [items, lookups.events]
  );

  if (items.length === 0) {
    return (
      <EmptyState
        variant='empty'
        title='No activity yet'
        description='Timeline events will appear here'
        size='sm'
      />
    );
  }

  return (
    <div className='space-y-0'>
      <div>
        {tree.map((node, index) => (
          <TimelineTreeNodeView
            key={treeNodeKey(node, index)}
            node={node}
            depth={0}
            entityType={entityType}
          />
        ))}
      </div>

      {/* Sentinel element for infinite scroll */}
      <div ref={sentinelRef} className='h-4' />

      {/* Loading indicator */}
      {isFetchingNextPage && (
        <div className='flex justify-center py-4'>
          <LoadingIndicator size='xs' />
        </div>
      )}

      {/* Load more button (fallback for auto-scroll) */}
      {hasNextPage && !isFetchingNextPage && (
        <div className='flex justify-center py-4'>
          <button
            onClick={onLoadMore}
            className='text-sm text-fg-brand-primary hover:text-utility-brand-700 underline'
          >
            Load more events
          </button>
        </div>
      )}

      {/* End of timeline footer. When the contact's related email threads were
          capped, show the truncation notice instead of "Beginning of timeline"
          — the latter would be misleading because older threads exist. */}
      {!hideEndOfTimelineMessage &&
        !hasNextPage &&
        !isFetchingNextPage &&
        items.length > 0 &&
        (relatedThreadsTruncated ? (
          <RelatedThreadsTruncatedNotice />
        ) : (
          <div className='flex justify-center py-4'>
            <span className='text-sm text-quaternary'>
              Beginning of timeline
            </span>
          </div>
        ))}
    </div>
  );
}

// Error boundary for individual items
interface ItemErrorBoundaryProps {
  children: ReactNode;
  item: TimelineItem;
  entityType: string;
}

interface ItemErrorBoundaryState {
  hasError: boolean;
}

class ItemErrorBoundary extends Component<
  ItemErrorBoundaryProps,
  ItemErrorBoundaryState
> {
  constructor(props: ItemErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ItemErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const firstEvent = this.props.item.events[0];
    console.error('Item render error:', {
      error,
      errorInfo,
      item_type: this.props.item.type,
      event_type: firstEvent?.event_type_name,
      event_id: firstEvent?.event_id,
    });
  }

  render() {
    if (this.state.hasError) {
      const firstEvent = this.props.item.events[0];
      if (firstEvent) {
        return (
          <FallbackEvent
            event={firstEvent}
            entityType={this.props.entityType}
          />
        );
      }
      return null;
    }
    return this.props.children;
  }
}
